import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import {
  keys as contactKeys,
  useCreateIncident205aContact,
  useIncident205aContacts,
  useUpdateIncident205aContact,
  type Incident205aContact,
} from 'components/IAP/hooks/incident205aContactHooks';
import {
  useIncident,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useIncidentRoster } from 'components/IAP/hooks/incidentRoster/useIncidentRoster';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import config from 'constants/apiConfig';
import { selectUserGuid } from 'slices/commonSelectors';
import { incidentCardCommunicationsList205aTitle } from '../../USCGPage';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from '../icsBoxPrimitives';
import { OperationalPeriodScheduleControl } from '../OperationalPeriodScheduleControl';
import Ics205aSignatureBox from './Ics205aSignatureBox';
import {
  ICS205A_BOX_LABELS,
  ICS205A_CONTACTS_HINT,
  ICS205A_INCIDENT_LOCATION_HINT,
  ICS205A_INCIDENT_NAME_HINT,
  ICS205A_OPERATIONAL_PERIOD_HINT,
} from './ics205aBoxLabels';
import {
  cloneContacts,
  contactChanged,
  contactKey,
  createTempContact,
  serializeContactsDraft,
  toDraftContact,
} from './draftUtils';
import { Ics205aContactRowsTable } from './Ics205aContactRowsTable';
import { ICS205A_ICON_BTN, ICS205A_OUTLINE_BTN } from './ics205aStyles';
import type { Ics205aContactPatch, Ics205aDraftContact } from './types';

const bySortThenNewest = (a: Ics205aDraftContact, b: Ics205aDraftContact) => {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return 0;
};

/**
 * Communications List, the ICS 205A-CG.
 *
 * Boxes 1-3 are the read-only header, box 4 is the contact table and box 5 is
 * the Communications Unit Leader's signature. Unlike the other cards the table
 * holds a draft of every row and commits them together, so one Save is one
 * version regardless of how many rows moved.
 */
const Ics205aStandaloneForm: FC = () => {
  const { data: contacts } = useIncident205aContacts();
  const { data: roster } = useIncidentRoster();
  const { data: incident } = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const { data: incidentPermissions } = useIncidentPermissions();
  const canEdit = incidentPermissions?.['incident:editCards'] ?? false;
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();

  const { effectivePeriodNumber, isFuture, readOnlyForms } = useICSContext();
  const { isMapVisible } = useUscgMapContext();

  const createContact = useCreateIncident205aContact();
  const updateContact = useUpdateIncident205aContact();
  const { exportAsForm } = useIcsFormPdfPreview();

  /**
   * The 205A is a versioned form, so a committed Save should leave a draft
   * version behind. One record per Save rather than per row: the whole box is
   * one edit as far as the printed form is concerned.
   */
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_205A',
    effectivePeriodNumber
  );

  const [draftContacts, setDraftContacts] = useState<Ics205aDraftContact[]>([]);
  const [baselineSnapshot, setBaselineSnapshot] = useState('');
  const [focusContactId, setFocusContactId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const serverContacts = useMemo(
    () =>
      (contacts ?? [])
        .filter((row) => !row.archived)
        .map(toDraftContact)
        .slice()
        .sort(bySortThenNewest),
    [contacts]
  );

  const serverByKey = useMemo(() => {
    const map = new Map<string, Incident205aContact>();
    for (const row of contacts ?? []) {
      map.set(row.contact_id, row);
    }
    return map;
  }, [contacts]);

  const isDirty = useMemo(
    () =>
      baselineSnapshot !== '' &&
      serializeContactsDraft(draftContacts) !== baselineSnapshot,
    [baselineSnapshot, draftContacts]
  );

  const draftRef = useRef(draftContacts);
  const baselineRef = useRef(baselineSnapshot);
  draftRef.current = draftContacts;
  baselineRef.current = baselineSnapshot;

  useEffect(() => {
    const dirty =
      baselineRef.current !== '' &&
      serializeContactsDraft(draftRef.current) !== baselineRef.current;

    if (dirty) {
      setDraftContacts((prev) => {
        const prevKeys = new Set(prev.map(contactKey));
        const additions = serverContacts.filter(
          (row) => !prevKeys.has(contactKey(row))
        );
        if (additions.length === 0) return prev;
        return cloneContacts([...prev, ...additions]);
      });
      return;
    }

    const next = cloneContacts(serverContacts);
    setDraftContacts(next);
    setBaselineSnapshot(serializeContactsDraft(next));
  }, [serverContacts]);

  const visibleDraftContacts = useMemo(
    () => draftContacts.filter((row) => !row.archived),
    [draftContacts]
  );

  const patchDraft = useCallback(
    (contact: Ics205aDraftContact, patch: Ics205aContactPatch) => {
      const key = contactKey(contact);
      setDraftContacts((prev) =>
        prev.map((row) =>
          contactKey(row) === key ? { ...row, ...patch } : row
        )
      );
    },
    []
  );

  const handleDeleteContact = useCallback((contact: Ics205aDraftContact) => {
    const key = contactKey(contact);
    setDraftContacts((prev) => {
      if (contact.isNew) {
        return prev.filter((row) => contactKey(row) !== key);
      }
      return prev.map((row) =>
        contactKey(row) === key ? { ...row, archived: true } : row
      );
    });
  }, []);

  const handleCancel = () => {
    const next = cloneContacts(serverContacts);
    setDraftContacts(next);
    setBaselineSnapshot(serializeContactsDraft(next));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const ops: Promise<unknown>[] = [];

      for (const draft of draftContacts) {
        const server = serverByKey.get(draft.contact_id);

        if (draft.isNew) {
          if (draft.archived) continue;
          ops.push(
            createContact.mutateAsync({
              assignment: draft.assignment,
              name: draft.name,
              cell_phone: draft.cell_phone,
              radio_frequency: draft.radio_frequency,
              other: draft.other,
              sort_order: draft.sort_order,
              archived: false,
            })
          );
          continue;
        }

        if (draft.archived) {
          if (server && !server.archived) {
            ops.push(
              updateContact.mutateAsync({
                contact_id: draft.contact_id,
                assignment: draft.assignment,
                name: draft.name,
                cell_phone: draft.cell_phone,
                radio_frequency: draft.radio_frequency,
                other: draft.other,
                sort_order: draft.sort_order,
                archived: true,
              })
            );
          }
          continue;
        }

        if (contactChanged(draft, server)) {
          ops.push(
            updateContact.mutateAsync({
              contact_id: draft.contact_id,
              assignment: draft.assignment,
              name: draft.name,
              cell_phone: draft.cell_phone,
              radio_frequency: draft.radio_frequency,
              other: draft.other,
              sort_order: draft.sort_order,
              archived: false,
            })
          );
        }
      }

      await Promise.all(ops);

      const fresh = await queryClient.fetchQuery({
        queryKey: contactKeys.list(incidentId, userGuid),
        queryFn: async () => {
          const resp = await axios.get<Incident205aContact[]>(
            `${config.apiGateway.ics_incident_205a_contacts}/${incidentId}`
          );
          return resp.data;
        },
      });

      const next = cloneContacts(
        (fresh ?? [])
          .filter((row) => !row.archived)
          .map(toDraftContact)
          .sort(bySortThenNewest)
      );
      setDraftContacts(next);
      setBaselineSnapshot(serializeContactsDraft(next));

      // Recorded after the refetch, so the server snapshots rows that are
      // already persisted rather than the ones still in flight.
      if (!readOnlyForms) recordVersionSave.mutate();
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddContact = () => {
    const nextSort =
      draftContacts.reduce(
        (max, row) => Math.max(max, row.sort_order ?? 0),
        -1
      ) + 1;
    const created = createTempContact(nextSort);
    setDraftContacts((prev) => [...prev, created]);
    setFocusContactId(created.contact_id);
  };

  const handleFillFromRoster = () => {
    const members = (roster ?? []).filter(
      (member) =>
        member.active !== 0 &&
        (member.userName || member.emailAddress) &&
        member.incidentPositions.length > 0
    );

    if (members.length === 0) return;

    const existingKeys = new Set(
      draftContacts
        .filter((row) => !row.archived)
        .map(
          (row) =>
            `${row.assignment.trim().toLowerCase()}|${row.name.trim().toLowerCase()}`
        )
    );

    let sort =
      draftContacts.reduce(
        (max, row) => Math.max(max, row.sort_order ?? 0),
        -1
      ) + 1;

    const seeded: Ics205aDraftContact[] = [];
    for (const member of members) {
      const assignment = member.incidentPositions
        .map((pos) => pos.positionName)
        .join(', ');
      const name = member.userName || member.emailAddress || '';
      const key = `${assignment.trim().toLowerCase()}|${name.trim().toLowerCase()}`;
      if (existingKeys.has(key)) continue;
      existingKeys.add(key);
      seeded.push(
        createTempContact(sort, {
          assignment,
          name,
          cell_phone: member.contact?.mobilePhone || '',
        })
      );
      sort += 1;
    }

    if (seeded.length === 0) return;
    setDraftContacts((prev) => [...prev, ...seeded]);
  };

  const handleExport = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm({
      formname: 'ICS_205A_CG',
      data: {},
      mapSketch: null,
      format,
    });
  };

  const exportActions = (
    <FormExportButton
      formname="ICS_205A_CG"
      onExport={handleExport}
      label="Preview ICS-205A"
    />
  );

  const body = (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <FormVersioningBanner baseId="ICS_205A" />

      <div className="tw:grid tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel={ICS205A_BOX_LABELS.incidentName}
          testId="ics205a-box-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS205A_BOX_LABELS.incidentName}>
              {ICS205A_INCIDENT_NAME_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incident?.name ?? ''}
            testId="ics205a-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS205A_BOX_LABELS.incidentLocation}
          testId="ics205a-box-incident-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS205A_BOX_LABELS.incidentLocation}>
              {ICS205A_INCIDENT_LOCATION_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incidentInfo?.incidentLocation || incident?.address || ''}
            testId="ics205a-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS205A_BOX_LABELS.operationalPeriod}
          testId="ics205a-box-operational-period"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS205A_BOX_LABELS.operationalPeriod}>
              {ICS205A_OPERATIONAL_PERIOD_HINT}
            </BoxInfo>
          }
        >
          <OperationalPeriodScheduleControl testId="ics205a-operational-period" />
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={ICS205A_BOX_LABELS.contacts}
        testId="ics205a-box-contacts"
        className="tw:mt-0 tw:min-w-0"
        headerAction={
          <BoxInfo label={ICS205A_BOX_LABELS.contacts}>
            {ICS205A_CONTACTS_HINT}
          </BoxInfo>
        }
      >
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
          {canEdit ? (
            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={ICS205A_OUTLINE_BTN}
                onClick={handleFillFromRoster}
              >
                Fill From Roster
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={ICS205A_OUTLINE_BTN}
                onClick={handleAddContact}
              >
                + Add Contact
              </Button>
            </div>
          ) : null}

          <Ics205aContactRowsTable
            contacts={visibleDraftContacts}
            canEdit={canEdit}
            focusContactId={focusContactId}
            onFocusConsumed={() => setFocusContactId(null)}
            onPatchContact={patchDraft}
            onDeleteContact={handleDeleteContact}
          />

          <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
            <span className="tw:text-sm tw:text-muted-foreground">
              {visibleDraftContacts.length} Contact
              {visibleDraftContacts.length === 1 ? '' : 's'}
            </span>
            {canEdit ? (
              <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className={ICS205A_ICON_BTN}
                  disabled={!isDirty || isSaving}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={ICS205A_OUTLINE_BTN}
                  disabled={!isDirty || isSaving}
                  onClick={() => {
                    void handleSave();
                  }}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </Ics204CardSection>

      <Ics205aSignatureBox periodNumber={effectivePeriodNumber} />
    </div>
  );

  // Beside the map on a future period there is no room for card chrome, so the
  // boxes stand on the panel itself, as the 202 does.
  if (isMapVisible && isFuture) {
    return <div className="dark content tw:text-foreground mt-2">{body}</div>;
  }

  return (
    <Card
      data-testid="uscg-205a-card"
      data-form-family="uscg"
      className="dark tw:bg-background tw:text-foreground"
    >
      <CardHeader>
        <CardTitle>{incidentCardCommunicationsList205aTitle}</CardTitle>
        <CardAction>{exportActions}</CardAction>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

export default Ics205aStandaloneForm;
